<template>
  <div>
    <div class="d-flex flex-wrap mb-2 px-2" style="gap:6px;">
      <v-chip x-small label dark color="#1565C0">{{ $t("message.professor") }}</v-chip>
      <v-chip x-small label dark color="#0288D1">{{ $t("message.assistant_professor") }}</v-chip>
      <v-chip x-small label dark color="#388E3C">{{ $t("message.event_participating") }}</v-chip>
      <v-chip x-small label dark color="#8d70b3">{{ $t("message.registered") }}</v-chip>
      <v-chip x-small label dark color="#4CAF50">{{ $t("message.event") }}</v-chip>
      <v-chip x-small label dark color="#fc9826">{{ $t("message.open") }}</v-chip>
      <v-chip x-small label dark color="#757575"><span style="font-size:10px;">&#9698;</span>&nbsp;{{ $t("message.other_academy") }}</v-chip>
      <v-chip x-small label dark color="#f44336">{{ $t("message.cancelled") }}</v-chip>
    </div>
    <div ref="calendar" class="calendar-container"></div>
    <v-dialog v-model="eventDialog" max-width="600px">
      <v-card v-if="selectedEvent">
        <v-card-title>
          {{ selectedEvent.title }}
          <v-spacer></v-spacer>
          <v-btn icon small @click="eventDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-list dense>
            <v-list-item v-if="selectedEvent.extendedProps.isProfessor || selectedEvent.extendedProps.isAssistantProfessor">
              <v-list-item-content>
                <v-chip small dark :color="selectedEvent.extendedProps.isProfessor ? '#1565C0' : '#0288D1'">
                  <v-icon small left>mdi-whistle</v-icon>
                  {{ selectedEvent.extendedProps.isProfessor ? $t("message.professor") : $t("message.assistant_professor") }}
                </v-chip>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="selectedEvent.extendedProps.isOtherAcademy">
              <v-list-item-content>
                <v-chip small outlined color="secondary">
                  <v-icon small left>mdi-school-outline</v-icon>
                  {{ selectedEvent.extendedProps.academyName || $t("message.other_academy") }}
                </v-chip>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="selectedEvent.extendedProps.professorUser">
              <v-list-item-content>
                <v-list-item-title>{{ $t("message.professor") }}</v-list-item-title>
                <v-list-item-subtitle>{{ selectedEvent.extendedProps.professorUser.displayName }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="selectedEvent.extendedProps.assistantProfessors && selectedEvent.extendedProps.assistantProfessors.length">
              <v-list-item-content>
                <v-list-item-title>{{ $t("message.assistant_professor") }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ selectedEvent.extendedProps.assistantProfessors.map(p => p.displayName || p).join(', ') }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>{{ $t("message.time") }}</v-list-item-title>
                <v-list-item-subtitle>{{ formatEventTime(selectedEvent) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="selectedEvent.extendedProps.maxCapacity">
              <v-list-item-content>
                <v-list-item-title>{{ $t("message.capacity") }}</v-list-item-title>
                <v-list-item-subtitle>
                  <span :class="selectedEvent.extendedProps.hasCapacity ? 'success--text' : 'error--text'">
                    {{ selectedEvent.extendedProps.approved ? selectedEvent.extendedProps.approved.length : 0 }}/{{
                      selectedEvent.extendedProps.maxCapacity }}
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="selectedEvent.extendedProps.status">
              <v-list-item-content>
                <v-list-item-title>{{ $t("message.status") }}</v-list-item-title>
                <v-list-item-subtitle>
                  <v-chip small :color="selectedEvent.extendedProps.status === 'approved' ? 'success' : 'warning'">
                    {{ selectedEvent.extendedProps.status === 'approved' ? $t("message.approved") :
                      $t("message.pending") }}
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn v-if="selectedEvent.extendedProps.canJoin" outlined color="primary" @click="handleJoinClass">
            {{ $t("message.join") }}
          </v-btn>
          <v-btn v-if="selectedEvent.extendedProps.canWaitlist" outlined color="secondary" @click="handleJoinClass">
            {{ $t("message.join_waitlist") }}
          </v-btn>
          <v-btn text @click="handleViewDetails" v-if="selectedEvent.id && selectedEvent.id.indexOf('_') < 0">
            {{ $t("message.view_details") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'CalendarDisplay',
  props: {
    events: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      calendar: null,
      eventDialog: false,
      selectedEvent: null
    };
  },
  watch: {
    events: {
      handler() {
        this.updateCalendarEvents();
      },
      deep: true
    },
    '$store.state.theme'() {
      if (this.calendar) {
        this.calendar.destroy();
        this.$nextTick(() => {
          this.initCalendar();
        });
      }
    }
  },
  mounted() {
    this.loadFullCalendar();
  },
  beforeDestroy() {
    if (this.calendar) {
      this.calendar.destroy();
    }
  },
  methods: {
    loadFullCalendar() {
      if (window.FullCalendar) {
        this.initCalendar();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/fullcalendar@6.1.4/index.global.min.js';
      script.onload = () => {
        this.initCalendar();
      };
      document.head.appendChild(script);
    },
    initCalendar() {
      if (!this.$refs.calendar || !window.FullCalendar) return;

      const isDark = this.$store.state.theme === 'dark';
      const vm = this;

      this.calendar = new window.FullCalendar.Calendar(this.$refs.calendar, {
        initialView: 'dayGridMonth',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,listMonth'
        },
        height: 'auto',
        events: function (_, successCallback) {
          successCallback(vm.getCalendarEvents());
        },
        eventClick: (info) => {
          vm.selectedEvent = info.event;
          vm.eventDialog = true;
        },
        eventClassNames: () => {
          return isDark ? ['fc-event-dark'] : [];
        },
        dayCellClassNames: () => {
          return isDark ? 'fc-day-dark' : '';
        },
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false
        },
        locale: vm.$i18n.locale || 'en',
        firstDay: 1,
        nowIndicator: true,
        navLinks: false,
        editable: false,
        selectable: false,
        selectMirror: false,
        dayMaxEvents: 3,
        displayEventTime: true,
        displayEventEnd: false,
        eventDisplay: 'block',
        eventContent: function (arg) {
          const timeText = arg.timeText;
          const title = arg.event.title;
          const props = arg.event.extendedProps;
          const cancelledLabel = vm.$t('message.cancelled');

          // We add a specific class 'fc-stacked-event' to handle the layout
          let frameClass = 'fc-event-main-frame fc-stacked-event';
          if (props.disabled) frameClass += ' fc-event-cancelled';
          let html = '<div class="' + frameClass + '">';
          if (timeText) {
            html += '<div class="fc-event-time">' + timeText + '</div>';
          }
          html += '<div class="fc-event-title-container">';
          html += '<div class="fc-event-title">';
          if (props.isOtherAcademy) {
            html += '<span class="fc-other-academy-marker" title="' + (props.academyName || 'Other Academy') + '">&#9698; </span>';
          }
          if (props.disabled) {
            html += '<span class="fc-event-cancelled-label">' + cancelledLabel + ': </span>';
          }
          html += title;
          html += '</div>';
          html += '</div></div>';

          return { html: html };
        }
      });

      this.calendar.render();
      this.applyCustomStyles();
    },
    getCalendarEvents() {
      if (!this.events || !Array.isArray(this.events)) {
        return [];
      }

      const calendarEvents = this.events
        .filter(event => event.date)
        .map(event => {
          const eventDate = new Date(event.date);

          if (isNaN(eventDate.getTime())) {
            return null;
          }

          const endDate = new Date(eventDate);

          if (event.startTimeHours !== undefined && !isNaN(event.startTimeHours)) {
            eventDate.setHours(event.startTimeHours, event.startTimeMinutes || 0, 0, 0);

            if (event.endTimeHours !== undefined && !isNaN(event.endTimeHours)) {
              endDate.setHours(event.endTimeHours, event.endTimeMinutes || 0, 0, 0);
            } else {
              endDate.setHours(event.startTimeHours + 1, event.startTimeMinutes || 0, 0, 0);
            }
          } else if (event.start !== undefined && !isNaN(event.start)) {
            const startH = Math.floor(event.start);
            const startM = Math.round((event.start - startH) * 100);
            eventDate.setHours(startH, startM, 0, 0);
            endDate.setHours(startH + 1, startM, 0, 0);
          } else {
            endDate.setHours(eventDate.getHours() + 1);
          }

          let eventColor;
          let eventBorderColor = null;
          let eventTextColor = '#ffffff';

          if (event.disabled) {
            eventColor = '#f44336';
            eventBorderColor = null;
            eventTextColor = '#ffffff';
          } else if (event.isProfessor) {
            eventColor = '#1565C0';
          } else if (event.isAssistantProfessor) {
            eventColor = '#0288D1';
          } else if (event.isEventParticipant) {
            eventColor = '#388E3C';
          } else if (event.social) {
            eventColor = '#4CAF50';
          } else if (event.status === 'approved' || event.status === 'pending') {
            eventColor = '#8d70b3';
          } else if (event.allowNonMembers) {
            eventColor = '#fc9826';
          } else if (event.isOtherAcademy) {
            eventColor = '#757575';
          } else {
            eventColor = '#9e9e9e';
          }

          const calEvent = {
            id: event._id,
            title: event.title || 'Untitled Event',
            start: eventDate.toISOString(),
            end: endDate.toISOString(),
            backgroundColor: eventColor,
            borderColor: eventBorderColor || eventColor,
            textColor: eventTextColor,
            extendedProps: {
              professorUser: event.professorUser,
              assistantProfessors: event.assistantProfessors,
              maxCapacity: event.maxCapacity,
              approved: event.approved,
              pending: event.pending,
              status: event.status,
              canJoin: event.canJoin,
              canWaitlist: event.canWaitlist,
              hasCapacity: event.hasCapacity,
              allowNonMembers: event.allowNonMembers,
              social: event.social,
              isProfessor: event.isProfessor,
              isAssistantProfessor: event.isAssistantProfessor,
              isEventParticipant: event.isEventParticipant,
              isOtherAcademy: event.isOtherAcademy,
              academyName: event.academyName,
              disabled: event.disabled,
              originalEvent: event
            }
          };

          return calEvent;
        })
        .filter(event => event !== null);

      return calendarEvents;
    },
    updateCalendarEvents() {
      if (!this.calendar) return;
      this.calendar.refetchEvents();
    },
    getEventColor(event) {
      if (!event) return '#616161';
      return event.color || (event.social ? '#f44336' : '#616161');
    },
    applyCustomStyles() {
      const style = document.createElement('style');
      const isDark = this.$store.state.theme === 'dark';

      style.innerHTML = `
        .calendar-container { font-family: 'Roboto', sans-serif; }
        
        .fc {
          ${isDark ? `
            --fc-border-color: rgba(255, 255, 255, 0.12);
            --fc-bg-event-opacity: 1;
          ` : `
            --fc-border-color: rgba(0, 0, 0, 0.12);
            --fc-bg-event-opacity: 1;
          `}
        }

        .fc-day-dark { background-color: ${isDark ? '#1e1e1e' : '#ffffff'}; }

        /* COLUMN HEADERS (Mon, Tue, Wed...) */
        .fc .fc-col-header-cell-cushion {
          color: ${isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)'} !important;
          text-decoration: none !important;
          font-weight: 600;
        }
        .fc .fc-col-header-cell {
          background-color: ${isDark ? '#2a2a2a' : '#f5f5f5'} !important;
          border-bottom: 2px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'} !important;
        }

        /* TOOLBAR & BUTTONS */
        .fc .fc-toolbar-title { font-size: 1.3em; font-weight: 600; color: ${isDark ? '#ffffff' : 'inherit'}; }
        .fc .fc-button {
          background-color: ${isDark ? '#424242' : '#ffffff'};
          border-color: ${isDark ? '#616161' : 'rgba(0, 0, 0, 0.12)'};
          color: ${isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)'};
        }
        .fc .fc-button-active { background-color: #000000 !important; color: #ffffff !important; }

        /* ========================================= */
        /* GRID VIEW STYLES (The colored boxes)      */
        /* ========================================= */
        
        /* Target only events inside the DayGrid (Month View) */
        .fc-daygrid-event {
          white-space: normal !important;
          border-radius: 4px !important;
          padding: 2px 4px !important;
        }

        .fc-daygrid-event .fc-event-main-frame {
          display: flex !important;
          flex-direction: column !important;
          align-items: flex-start !important;
        }

        /* Keep these white only for Grid Mode */
        .fc-daygrid-event .fc-event-time,
        .fc-daygrid-event .fc-event-title {
          color: #ffffff !important; 
          display: block !important;
          line-height: 1.1 !important;
        }

        .fc-daygrid-event .fc-event-time { font-weight: 700; font-size: 0.8em; }
        .fc-daygrid-event .fc-event-title { font-size: 0.9em; }

        /* ========================================= */
        /* LIST VIEW STYLES (The agenda view)        */
        /* ========================================= */

        .fc-list-event { cursor: pointer; }
        
        /* Target title and time specifically within list-item rows */
        .fc .fc-list-event-title,
        .fc .fc-list-event-title a {
          color: ${isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)'} !important;
          text-decoration: none !important;
        }

        .fc .fc-list-event-time {
          color: ${isDark ? '#bdbdbd' : 'rgba(0, 0, 0, 0.6)'} !important;
        }

        .fc .fc-list-day-cushion {
          background-color: ${isDark ? '#303030' : '#f5f5f5'} !important;
        }

        .fc .fc-list-day-text, 
        .fc .fc-list-day-side-text {
          color: ${isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)'} !important;
        }

        /* CANCELLED CLASSES — background is error color (set via JS); keep text white */
        .fc-event-cancelled .fc-event-time,
        .fc-event-cancelled .fc-event-title {
          color: #ffffff !important;
          text-decoration: line-through;
          opacity: 0.9;
        }
        .fc-event-cancelled-label {
          font-weight: 700;
          color: #ffffff !important;
          text-decoration: none !important;
          margin-right: 2px;
        }

        /* RESPONSIVE */
        @media (max-width: 767px) {
          .fc .fc-daygrid-day-frame { min-height: 100px !important; }
          .fc .fc-toolbar { flex-direction: column; gap: 8px; }
        }
      `;

      const existingStyle = document.getElementById('calendar-custom-styles');
      if (existingStyle) existingStyle.remove();
      style.id = 'calendar-custom-styles';
      document.head.appendChild(style);
    },
    formatEventTime(event) {
      if (!event || !event.start) return '';

      const start = new Date(event.start);
      const end = event.end ? new Date(event.end) : null;

      const timeFormat = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      };

      const startStr = start.toLocaleTimeString(this.$i18n.locale || 'en', timeFormat);
      if (end) {
        const endStr = end.toLocaleTimeString(this.$i18n.locale || 'en', timeFormat);
        return `${startStr} - ${endStr}`;
      }

      return startStr;
    },
    handleJoinClass() {
      if (this.selectedEvent && this.selectedEvent.extendedProps.originalEvent) {
        this.$emit('join-class', this.selectedEvent.extendedProps.originalEvent);
        this.eventDialog = false;
      }
    },
    handleViewDetails() {
      if (this.selectedEvent && this.selectedEvent.extendedProps.originalEvent) {
        this.$emit('view-event', this.selectedEvent.extendedProps.originalEvent);
        this.eventDialog = false;
      }
    }
  }
};
</script>

<style scoped>
.calendar-container {
  width: 100%;
  padding: 10px;
}

.fc-daygrid-event .fc-event-title,
.fc-daygrid-event .fc-event-time {
  color: #ffffff !important;
}

/* Force the vertical stack */
.fc-stacked-event {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  padding: 2px 0;
}

/* Remove the default margin and make time smaller/distinct */
.fc-event-time {
  font-size: 0.8em !important;
  margin-right: 0 !important;
  margin-bottom: 2px;
  opacity: 0.9;
  color: #ffffff !important;
}

.fc-event-title {
  white-space: normal !important;
  /* Allows title to wrap if still too long */
  line-height: 1.1;
  font-size: 0.9em;
}

/* Adjust cell height for small screens so stacked events don't clip */
@media (max-width: 600px) {
  .fc .fc-daygrid-day-frame {
    min-height: 100px !important;
  }

  .fc-event {
    padding: 4px 2px !important;
  }
}
</style>
